/**
 * Runtime configuration for the BIOS frontend.
 *
 * Resolution order (first non-empty wins):
 *
 *   1. window.__APP_CONFIG__         set by /runtime-config.js, regenerated
 *                                    at container start from the runtime
 *                                    environment (scripts/write-runtime-config.js).
 *                                    This is what makes one image SHA promote
 *                                    from staging to prod with no rebuild.
 *
 *   2. import.meta.env.REACT_APP_*   Vite inlines these at `npm run build` /
 *                                    `npm run dev` time. Used in local dev
 *                                    where the runtime-config.js stub is
 *                                    empty. Vite's `envPrefix: "REACT_APP_"`
 *                                    in vite.config.js keeps the .env keys
 *                                    backwards-compatible with the CRA
 *                                    naming convention.
 *
 *   3. hardcoded dev fallback        last-resort safety net so a forgotten
 *                                    env var fails loud against the dev
 *                                    backend instead of silently against
 *                                    prod.
 */
const runtime = (typeof window !== "undefined" && window.__APP_CONFIG__) || {};
const env = import.meta.env || {};

export const BASE_URL =
  runtime.BASE_URL ||
  env.REACT_APP_API_BASE_URL ||
  "https://www.development.backend.biosapplication.com/api/v1";

/**
 * Authentication transport.
 *
 *   "cookie"  backend issues an httpOnly Secure SameSite cookie on login;
 *             the browser attaches it automatically. The frontend never
 *             touches a token, which closes off XSS token-theft.
 *   "bearer"  legacy mode: the access token is read from localStorage and
 *             sent as `Authorization: Bearer ...`. Default for now so
 *             nothing breaks before the backend ships cookie auth.
 *
 * Flip to "cookie" once the backend sets the cookie + accepts CSRF tokens.
 */
const rawAuthMode = (runtime.AUTH_MODE || env.REACT_APP_AUTH_MODE || "bearer").toLowerCase();
export const AUTH_MODE = rawAuthMode === "cookie" ? "cookie" : "bearer";

/**
 * Public site URL. Used as the base for per-route canonical links and
 * Open Graph URLs (src/components/SeoHead). The trailing slash is stripped
 * so route paths can join cleanly: `${SITE_URL}${pathname}`.
 *
 * In prod the runtime config sets
 * `SITE_URL=https://www.biosapplication.com`. The dev fallback points at
 * the local Vite server so canonicals render against the running app and
 * never leak a fake domain into a snapshot or screenshot.
 */
export const SITE_URL = (
  runtime.SITE_URL ||
  env.REACT_APP_SITE_URL ||
  "http://localhost:3000"
).replace(/\/+$/, "");
